import { useDocumentVisibility } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { INotification, INotificationList, IUnreadCount } from '@/types/Notification'

/*
 * THE BELL'S COUNT AND LIST (docs/API.md → Notifications).
 *
 * The unread count is polled every 60 s, as the contract asks, and only while the tab is
 * being looked at: a console left open overnight has nobody to tell, and a tab brought back
 * to the front asks at once rather than showing an hour-old number. A failed poll changes
 * nothing — no toast for a background question nobody asked.
 */
const POLL_MS = 60_000

export const useNotificationsStore = defineStore('notifications', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()
    const visibility = useDocumentVisibility()

    const unread  = ref(0)
    const items   = ref<INotification[]>([])
    const loading = ref(false)
    const loaded  = ref(false)

    let timer: ReturnType<typeof setInterval> | undefined

    const loadCount = async () => {
        if (!appStore.isLoggedIn()) return

        const { statusCode, data } = await fetchStore.fetch('/notifications/unread-count').get().json<IUnreadCount>()

        if (statusCode.value === 200 && data.value) unread.value = data.value.unread
    }

    const loadList = async (): Promise<boolean> => {
        loading.value = true

        const { statusCode, data } = await fetchStore.fetch('/notifications?limit=50').get().json<INotificationList>()

        loading.value = false

        if (statusCode.value !== 200 || !data.value) return false

        items.value  = data.value.items ?? []
        unread.value = data.value.unread
        loaded.value = true

        return true
    }

    /** One notification read — optimistic: the dot goes at once, the request follows. */
    const markRead = async (notification: INotification) => {
        if (notification.read_at) return

        notification.read_at = new Date().toISOString()
        unread.value         = Math.max(0, unread.value - 1)

        const { statusCode } = await fetchStore.fetch(`/notifications/${notification.id}/read`).post().json()

        if (statusCode.value !== 204) await loadCount()
    }

    const markAllRead = async (): Promise<boolean> => {
        const { statusCode } = await fetchStore.fetch('/notifications/read-all').post().json()

        if (statusCode.value !== 204) return false

        const now = new Date().toISOString()

        items.value.forEach((item) => { if (!item.read_at) item.read_at = now })
        unread.value = 0

        return true
    }

    const start = () => {
        if (timer) return

        void loadCount()

        timer = setInterval(() => {
            if (visibility.value === 'hidden') return

            void loadCount()
        }, POLL_MS)
    }

    const stop = () => {
        if (timer) clearInterval(timer)

        timer = undefined
    }

    watch(visibility, (state) => {
        if (state === 'visible' && timer) void loadCount()
    })

    // Signing out throws the count away, like every other per-session state.
    watch(() => appStore.JWT, (jwt) => {
        if (jwt) return

        stop()
        unread.value = 0
        items.value  = []
        loaded.value = false
    })

    return { unread, items, loading, loaded, loadCount, loadList, markRead, markAllRead, start, stop }
})
